/**
 * components/locked-countdown.tsx
 *
 * Live countdown timer showing when a time-locked note opens.
 * Updates every second. Shows "Opening soon…" once the time passes.
 */
import { useEffect, useState } from 'react'

interface LockedCountdownProps {
  unlockAt: number
}

export function LockedCountdown({ unlockAt }: LockedCountdownProps) {
  const [timeLeft, setTimeLeft] = useState(() => {
    const diff = unlockAt * 1000 - Date.now()
    if (diff <= 0) return null
    const totalSec = Math.floor(diff / 1000)
    return {
      days: Math.floor(totalSec / 86400),
      hours: Math.floor((totalSec % 86400) / 3600),
      minutes: Math.floor((totalSec % 3600) / 60),
      seconds: totalSec % 60,
    }
  })

  useEffect(() => {
    const tick = () => {
      const diff = unlockAt * 1000 - Date.now()
      if (diff <= 0) {
        setTimeLeft(null)
        return
      }
      const totalSec = Math.floor(diff / 1000)
      setTimeLeft({
        days: Math.floor(totalSec / 86400),
        hours: Math.floor((totalSec % 86400) / 3600),
        minutes: Math.floor((totalSec % 3600) / 60),
        seconds: totalSec % 60,
      })
    }
    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [unlockAt])

  if (!timeLeft) {
    return <span className="text-[#B89AA2] italic">Opening soon…</span>
  }

  const parts: string[] = []
  if (timeLeft.days > 0) parts.push(`${timeLeft.days}d`)
  if (timeLeft.hours > 0 || timeLeft.days > 0) parts.push(`${timeLeft.hours}h`)
  parts.push(`${timeLeft.minutes}m`)
  if (timeLeft.days === 0) parts.push(`${timeLeft.seconds}s`)

  const exactTime = new Date(unlockAt * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  return (
    <span>
      <span className="text-[#D4537E] font-medium">{parts.join(' ')}</span>
      <span className="text-[#B89AA2]"> · opens </span>
      <span className="text-[#D4537E] font-medium">{exactTime}</span>
    </span>
  )
}
